import { Controller } from "@hotwired/stimulus";

// Mirrors Monster::CreationGuide: shows read-only LB3 reference values that update live as the
// user edits DV / size / concept. CA/JP/XP and the attack line come from DV (+ size for CA),
// Moral from the concept, and base movement from the size. Purely informational hints — nothing
// is auto-filled, the user always types the final numbers.
// Connects to data-controller="monster-creation-guide"
export default class extends Controller {
	static targets = ["dv", "size", "concept", "caHint", "jpHint", "xpHint", "moHint", "mvHint", "attacksHint", "attacksPanel"];
	static values = {
		guide: Object,
		caTemplate: String,
		jpTemplate: String,
		xpTemplate: String,
		baTemplate: String,
		attacksTemplate: String,
		naturalDamageTemplate: String,
		moTemplate: String,
		mvTemplate: String,
	};

	connect() {
		this.update();
	}

	update() {
		this.updateMorale();
		this.updateMovement();

		const suggestion = this.suggestionFor(this.dvTarget.value, this.sizeTarget.value);
		this.toggleDvHints(Boolean(suggestion));
		if (!suggestion) return;

		this.caHintTarget.textContent = this.caTemplateValue.replace("{{value}}", this.formatRange(suggestion.ca));
		this.jpHintTarget.textContent = this.jpTemplateValue.replace("{{value}}", suggestion.jp);
		this.xpHintTarget.textContent = this.xpTemplateValue.replace("{{value}}", suggestion.xpBase);

		const attacksText = this.attacksTemplateValue.replace("{{value}}", suggestion.attacks);
		const naturalDamageText = this.naturalDamageTemplateValue.replace("{{value}}", suggestion.naturalDamage);
		const baText = this.baTemplateValue.replace("{{value}}", this.formatBonusRange(suggestion.ba));
		this.attacksHintTarget.textContent = [attacksText, naturalDamageText, baText].join(", ");
	}

	// Moral (2–12): a typical range keyed to the creature's concept, labelled with its name.
	updateMorale() {
		if (!this.hasConceptTarget) return; // a variation has no concept field: it reads its parent's
		const range = this.guideValue.morale_by_concept[this.conceptTarget.value];
		const text = range && this.moTemplateValue.replace("{{concept}}", this.selectedText(this.conceptTarget)).replace("{{value}}", this.formatRange(range));
		this.setHint(this.moHintTarget, text);
	}

	// Base movement: the usual value for the creature's size, labelled with its name.
	updateMovement() {
		const value = this.guideValue.movement_by_size[this.sizeTarget.value];
		const text = value != null && this.mvTemplateValue.replace("{{size}}", this.selectedText(this.sizeTarget)).replace("{{value}}", value);
		this.setHint(this.mvHintTarget, text);
	}

	selectedText(select) {
		return select.selectedOptions[0]?.text ?? "";
	}

	toggleDvHints(visible) {
		for (const hint of [this.caHintTarget, this.jpHintTarget, this.xpHintTarget, this.attacksPanelTarget]) {
			hint.classList.toggle("hidden", !visible);
		}
	}

	setHint(hint, text) {
		hint.classList.toggle("hidden", !text);
		if (text) hint.textContent = text;
	}

	// Mirrors Monster::CreationGuide.suggestions_for using the bracket table serialized
	// into the guideValue (see Monster::CreationGuide.as_json).
	suggestionFor(dvValue, size) {
		if (!/^\d+$/.test(dvValue)) return null;

		const dv = Number.parseInt(dvValue, 10);
		const bracket = this.findBracket(this.guideValue.brackets, dv);
		const jpBracket = this.findBracket(this.guideValue.jp_brackets, dv);
		if (!bracket || !jpBracket) return null;

		return {
			ca: this.guideValue.larger_sizes.includes(size) ? bracket.ca_larger : bracket.ca_agile,
			ba: bracket.ba,
			attacks: bracket.attacks,
			naturalDamage: bracket.natural_damage,
			jp: jpBracket.jp,
			xpBase: this.xpBaseFor(dv),
		};
	}

	findBracket(brackets, dv) {
		return brackets.find((bracket) => dv >= bracket.min && (bracket.max === null || dv <= bracket.max));
	}

	xpBaseFor(dv) {
		const { xp_base: base, xp_formula: formula } = this.guideValue;
		return dv >= formula.from_dv ? formula.base + formula.per_dv * (dv - formula.from_dv) : base[dv];
	}

	formatRange([min, max]) {
		if (max === null || max === undefined) return `${min}+`;
		return min === max ? `${min}` : `${min}–${max}`;
	}

	formatBonusRange([min, max]) {
		if (max === null || max === undefined) return `+${min} ou mais`;
		return min === max ? `+${min}` : `+${min} a +${max}`;
	}
}
